import JsonURL from '@jsonurl/jsonurl';
import { EllipsisIcon } from 'components/common/Icons';
import { ChatBubble } from 'components/core';
import { IChatBubble } from 'components/core/ChatBubble/ChatBubble';
import { chatExamples } from 'components/core/ChatWidget/constants';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FilterContext, ViewContext } from 'utils/context';
import { paramsFromShipment } from 'utils/context/FilterContext/paramsShipments';
import { TFilter } from 'utils/context/FilterContext/types';
import { useChatFeedback, useChats } from 'utils/hooks';
import { TChat } from 'utils/hooks/useChats/fetchChats';
import { v4 as uuidv4 } from 'uuid';

import {
  Button,
  Flex,
  IconButton,
  reactIcons as Icons,
  Tag,
  TextArea,
  Typography,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';
import { useSegment, useUser } from '@project44/ui-utils';
import { IShipmentSearchCriteria } from '@project44-models/shipment-list';

const styles = {
  root: {
    bgColor: '$palette-grey-900',
    bottom: 0,
    left: 72,
    position: 'absolute',
    width: 384,
    zIndex: 99,
    '& button:hover': {
      backgroundColor: '$palette-grey-800',
      color: '$text-contrast',
    },
    '& .manifest-textfield-base__input': {
      backgroundColor: '$palette-grey-700',
      color: 'white',
      border: 'none',
      borderRadius: 0,
      minHeight: '40px',
    },
  },
  chatbox: {
    borderTop: '1px solid $palette-grey-700',
    flexGrow: 1,
    padding: '$medium',
    overflow: 'scroll',
  },
  tag: {
    bgColor: '$background-primary',
    color: '$primary-default',
  },
  error: {
    bgColor: '$palette-red-50',
    padding: '$x-small',
  },
  controls: {
    bgColor: '$palette-grey-800',
    padding: '$small $medium',
    '& button:hover': {
      color: '$text-primary',
      backgroundColor: '$palette-indigo-200',
    },
  },
};

interface IChatWidget {
  doSearch: () => void;
  isHidden: boolean;
  onClose: () => void;
}

function ChatWidget({ doSearch, isHidden, onClose }: IChatWidget) {
  const [chatHistory, setChatHistory] = useState<IChatBubble[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [discussionId] = useState(uuidv4);
  const [value, setValue] = useState('');
  const [scrollTo, setScrollTo] = useState<number | null>(null);
  const analytics = useSegment();
  const inputRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data, getChats, isError, isLoading } = useChats();
  const { data: user } = useUser();
  const { filters, setFilters, setFiltersKey } = useContext(FilterContext);
  const { savedShipments } = useContext(ViewContext);
  const { t } = useTranslation();
  const { sendChatFeedback } = useChatFeedback();

  const isUser = ({ isBot, isInfo, isHeader }: IChatBubble) =>
    !isBot && !isInfo && !isHeader;

  const handleRate = async (data: TChat, isApproved: boolean) => {
    const rating = isApproved ? 'ACCEPTED' : 'REJECTED';
    const shipmentFilter = data.shipmentFilter
      ? {
          filterParams: data.shipmentFilter.filterCriteria,
        }
      : undefined;
    await sendChatFeedback({
      chatId: data.id,
      feedback: rating,
      discussionId,
      shipmentFilter,
      searchUrl: JsonURL.stringify(
        paramsFromShipment(data.shipmentFilter?.filterCriteria || {}),
        { AQF: true },
      ),
    });
    analytics.track({
      eventName: `MOVEMENT_CHAT_FILTER_${rating}`,
      properties: {
        responseId: data.id,
        discussionId,
        user: user?.username,
        filterParams: filters,
        filterCriteria: data.shipmentFilter?.filterCriteria,
      },
    });
    setChatHistory((prev) => {
      const idx = prev.findIndex((foo) => foo.id === data.id);
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        isRatedGood: isApproved,
        isRatedBad: !isApproved,
      };
      return next;
    });
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleChange = (next: string) => {
    setValue(next);
  };

  const handleChat = () => {
    if (value) {
      const id = uuidv4();
      const { firstName, lastName } = user || {};
      const initials = [firstName, lastName]
        .map((name) => name?.substring(0, 1))
        .join('');
      const icon = initials ? <div>{initials}</div> : <Icons.Person />;
      const next = [
        ...chatHistory,
        { createdAt: new Date(), icon, id, message: value },
      ];
      const messages = next.filter(isUser).map(({ message }) => message);
      setChatHistory(next);
      getChats({ id, discussionId, inputHistory: messages, role: 'USER' });
      setValue('');
      setScrollTo(99999);
    }
  };

  const handleFilters = useCallback(
    (filterCriteria: TFilter) => {
      setFilters({ listView: 'all-shipments', ...filterCriteria });
      doSearch();
    },
    [doSearch, setFilters],
  );

  const handleKeyDown = (key: React.KeyboardEvent<HTMLInputElement>) => {
    // Enter Key
    if (key.keyCode === 13 && !key.shiftKey) {
      handleChat();
    }
  };

  const handleSave = async (shipmentFilter?: {
    name: string;
    filterCriteria: IShipmentSearchCriteria;
  }) => {
    if (shipmentFilter) {
      const { views } = await savedShipments.createView(shipmentFilter);
      if (views && views[0]?.id) {
        setFiltersKey('listView', views[0].id);
        setChatHistory((prev) => {
          const idx = prev.findIndex((foo) => foo.id === data?.id);
          const next = [...prev];
          next[idx] = { ...next[idx], isSaved: true };
          return next;
        });
      }
    }
  };

  const handleUndo = () => {
    const cache = chatHistory[chatHistory.length - 1];
    if (cache.handleRestore) {
      cache.handleRestore();
    }
  };

  useEffect(() => {
    if (data) {
      const criteria = data?.shipmentFilter?.filterCriteria;
      const params = paramsFromShipment(criteria || {});
      let onFilter, onSave, onRate, onRestore;
      if (criteria) {
        onFilter = () => handleFilters(params);
        onRate = (isApproved: boolean) => handleRate(data, isApproved);
        onRestore = () => handleFilters(filters);
        onSave = () => handleSave(data.shipmentFilter);
      }
      const next = {
        createdAt: new Date(),
        filterName: data.shipmentFilter?.name,
        handleFilters: onFilter,
        handleRate: onRate,
        handleRestore: onRestore,
        handleSave: onSave,
        icon: <Icons.Cube css={{ color: '$text-contrast' }} />,
        id: data.id,
        isBot: true,
        message: data.chatResponse,
      };
      setChatHistory((prev) => [...prev, next]);
      setScrollTo(99999);
      if (next.handleFilters) {
        next.handleFilters();
      }
    }
    // TODO fix dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (scrollTo || scrollTo === 0) {
      setScrollTo(null);
      const input = inputRef.current?.children[0] as HTMLInputElement;
      input.focus();
      containerRef?.current?.scrollTo({ top: scrollTo, behavior: 'smooth' });
    }
  }, [scrollTo]);

  const chatBubbles = useMemo(
    () =>
      chatHistory.map((chat) => (
        <ChatBubble
          key={chat.id}
          ariaDetails={t('core.chat.userGeneratedFilter')}
          {...chat}
        />
      )),
    [chatHistory, t],
  );

  const chatTutorial = useMemo(() => {
    return chatExamples.map((example) => {
      const criteria = example.filterCriteria;
      const params = paramsFromShipment(criteria || {});
      const handle = criteria ? () => handleFilters(params) : undefined;
      return (
        <ChatBubble {...example} key={example.message} handleFilters={handle} />
      );
    });
  }, [handleFilters]);

  if (isHidden) {
    return null;
  }

  return (
    <Flex orientation="vertical" css={styles.root}>
      <Flex justify="between" css={{ padding: '$small $medium' }}>
        <Flex gap="small" align="center">
          <Typography variant="subtextBold" css={{ color: '$text-contrast' }}>
            {t('core.chat.movementGpt')}
          </Typography>
          <Tag css={styles.tag}>{t('core.chat.preview')}</Tag>
        </Flex>
        <Flex gap="small" align="center">
          <IconButton
            aria-label={t(`core.chat.${isCollapsed ? 'expand' : 'minimize'}`)}
            children={<Icons.Minus css={{ color: '$text-contrast' }} />}
            onPress={toggleCollapse}
            size="small"
            variant="tertiary"
          />
          <IconButton
            aria-label={t('core.chat.closeChat')}
            children={<Icons.Close css={{ color: '$text-contrast' }} />}
            onPress={onClose}
            size="small"
            variant="tertiary"
          />
        </Flex>
      </Flex>
      {!isCollapsed && (
        <Flex orientation="vertical" css={{ height: 550 }}>
          <Flex
            ref={containerRef}
            orientation="vertical"
            gap="medium"
            css={styles.chatbox}
          >
            {chatTutorial}
            {chatBubbles}
            {isError && (
              <ChatBubble
                message={t('core.chat.error')}
                isBot
                icon={<Icons.Cube css={{ color: '$text-contrast' }} />}
              />
            )}
            {isLoading && (
              <ChatBubble
                message={<EllipsisIcon size="36px" />}
                isBot
                icon={<Icons.Cube css={{ color: '$text-contrast' }} />}
              />
            )}
          </Flex>
          {!!chatHistory.filter((chat) => !isUser(chat)).length && (
            <Flex justify="between" css={styles.controls}>
              <Button
                aria-label={t('global.undo')}
                children={t('global.undo')}
                css={{ bgColor: '$palette-indigo-100' }}
                isDisabled={!chatHistory[chatHistory.length - 1]?.handleRestore}
                onPress={handleUndo}
                startIcon={<Icons.Reset />}
                size="small"
                variant="secondary"
              />
              <Flex gap="medium">
                <Button
                  aria-label={t('core.chat.newChat')}
                  children={t('core.chat.newChat')}
                  css={{ bgColor: '$palette-indigo-100' }}
                  size="small"
                  startIcon={<Icons.Add />}
                  variant="secondary"
                  onPress={() => {
                    setChatHistory([]);
                    setScrollTo(0);
                  }}
                />
              </Flex>
            </Flex>
          )}
          <Flex css={{ borderTop: '1px solid $palette-grey-600' }}>
            <TextArea
              ref={inputRef}
              aria-label={t('core.chat.writeMessage')}
              isDisabled={isLoading}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={t(`core.chat.${isLoading ? 'generating' : 'write'}`)}
              value={value}
            />
            {!!value && (
              <Button
                aria-label={t('core.chat.submitChat')}
                children={<Icons.ChevronRightDouble />}
                isDisabled={!value}
                onPress={handleChat}
                startIcon={<Typography variant="body">Enter</Typography>}
                css={{
                  backgroundColor: '$palette-grey-700',
                  color: 'white',
                  height: '100%',
                }}
              />
            )}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
}

export default ChatWidget;
